/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { Client } from "@modelcontextprotocol/sdk/client/index.js";
import { InMemoryTransport } from "@modelcontextprotocol/sdk/inMemory.js";
import { Transport } from "@modelcontextprotocol/sdk/shared/transport.js";
import { GoogleGenAI, mcpToTool } from '@google/genai';
import { ChatState, Playground } from './playground';
import { marked } from './src/utils/markdown';
import { leadService } from './src/services/lead-service';
import { campaignService } from './src/services/campaign-service';

import { startMcpGoogleMapServer } from './mcp_maps_server';

/* --------- */


async function startClient(transport: Transport) {
  const client = new Client({ name: "AI Studio", version: "1.0.0" });
  await client.connect(transport);
  return client;
}

/* ------------ */


const SYSTEM_INSTRUCTIONS = `you're an extremely proficient with map and discovering interesting places.
You can use tools to control the map.
When asked a question try to use tools to show related informations on the map.
Always explain what are you doing.`;

const EXAMPLE_PROMPTS = [
  'Where is something cool to see',
  'Show me San Francisco',
  'Where is a place with a tilted tower?',
  'Show me Mount Everest',
  'Can you show me Mauna Kea in Hawaii?',
  "Let's go to Venice, Italy.",
  'Take me to the northernmost capital city in the world',
  "How about the southernmost permanently inhabited settlement? What's it called and where is it?",
  'Show me the location of the ancient city of Petra in Jordan',
  "Let's jump to Machu Picchu in Peru",
  "Can you show me the Three Gorges Dam in China?",
  "Can you find a town or city with a really funny or unusual name and show it to me?"
];

/**
 * ============================================================================
 * O cliente do Gemini e criado SOB DEMANDA — nunca na avaliacao do modulo
 * ============================================================================
 *
 * Isto era:
 *
 *     const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY });
 *
 * no topo do modulo de ENTRADA. Dois problemas somados:
 *
 * 1. `process.env` nao existe no navegador. O Vite substitui a expressao por um
 *    objeto vazio, entao `apiKey` chegava `undefined`.
 * 2. O SDK lanca na CONSTRUCAO quando nao ha chave: "An API Key must be set when
 *    running in a browser".
 *
 * Como isso rodava no topo do modulo de entrada, a excecao acontecia ANTES do
 * `addEventListener('DOMContentLoaded')` la embaixo. O app nunca montava:
 * `leads.nyroh.com.br` servia uma pagina completamente EM BRANCO, com o erro
 * so no console. Verificado em Chromium sobre o `dist` compilado.
 *
 * Agora a construcao acontece na primeira mensagem enviada. Sem chave, o resto
 * do painel — que e a captacao, o que o app faz de fato — funciona, e so o chat
 * de IA acusa a falta com uma mensagem que diz qual variavel preencher.
 *
 * A chave continua sendo `VITE_*`, ou seja, publica para quem abrir o DevTools.
 * O destino e `POST /v1/ia/gerar` na API (item B-07).
 */
let clienteIa: GoogleGenAI | null = null;

function obterIa(): GoogleGenAI {
  if (clienteIa) return clienteIa;
  const chave = (import.meta as any).env?.VITE_GEMINI_API_KEY ?? '';
  if (!chave) {
    throw new Error(
      'Chat de IA indisponivel: falta VITE_GEMINI_API_KEY nas variaveis de build ' +
        'deste painel. O restante da captacao funciona sem ela.',
    );
  }
  clienteIa = new GoogleGenAI({ apiKey: chave });
  return clienteIa;
}

function createAiChat(mcpClient: Client) {
  return obterIa().chats.create({
    model: 'gemini-2.5-flash',
    config: {
      systemInstruction: SYSTEM_INSTRUCTIONS,
      tools: [mcpToTool(mcpClient)],
    },
  });
}

function camelCaseToDash(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
}

document.addEventListener('DOMContentLoaded', async (event) => {
  // Coordinated migration
  console.log('App: 🚀 Starting migration sequence...');
  try {
    console.log('App: [1/2] Migrating campaigns...');
    await campaignService.migrate();
    console.log('App: [2/2] Migrating leads...');
    await leadService.migrate();
    console.log('App: ✅ Migration sequence completed. Your data is safe in Supabase!');
  } catch (e) {
    console.error('App: ❌ Migration sequence failed', e);
  }

  const rootElement = document.querySelector('#root')! as HTMLElement;

  // Create Header
  const header = document.createElement('header');
  header.className = 'app-header';
  header.innerHTML = `
    <div class="app-logo">NYROH - LEADS</div>
    <nav class="top-menu">
      <div class="top-menu-item active" data-tab="mapa">Mapa</div>
      <div class="top-menu-item" data-tab="leads">Leads</div>
      <div class="top-menu-item" data-tab="regioes">Regiões</div>
      <div class="top-menu-item" data-tab="alvos">Alvos</div>
      <div class="top-menu-item" data-tab="campanhas">Campanhas</div>
      <div class="top-menu-item" data-tab="json-central">Central JSON</div>
    </nav>
  `;
  rootElement.appendChild(header);

  // Create Content Container
  const content = document.createElement('div');
  content.className = 'app-content';
  rootElement.appendChild(content);

  const playground = new Playground();
  playground.className = 'tab-panel active';
  content.appendChild(playground);

  import('./src/components/leads-dashboard');
  const leadsDashboard = document.createElement('leads-dashboard') as any;
  leadsDashboard.className = 'tab-panel';
  content.appendChild(leadsDashboard);

  import('./src/components/region-selector');
  const regionSelector = document.createElement('region-selector') as any;
  regionSelector.className = 'tab-panel';
  content.appendChild(regionSelector);

  import('./src/components/target-selector');
  const targetSelector = document.createElement('target-selector') as any;
  targetSelector.className = 'tab-panel';
  content.appendChild(targetSelector);

  import('./src/components/campaign-manager');
  const campaignManager = document.createElement('campaign-manager') as any;
  campaignManager.className = 'tab-panel';
  content.appendChild(campaignManager);

  import('./src/components/json-central-view');
  const jsonCentralView = document.createElement('json-central-view') as any;
  jsonCentralView.className = 'tab-panel';
  content.appendChild(jsonCentralView);

  playground.addEventListener('leads-updated', (e: any) => {
    // Only update dashboard leads if we are not on the leads tab
    const activeTab = header.querySelector('.top-menu-item.active')?.getAttribute('data-tab');
    if (activeTab !== 'leads') {
      leadsDashboard.leads = e.detail;
    }
  });

  playground.addEventListener('enrichment-updated', (e: any) => {
    leadsDashboard.isEnriching = e.detail.isEnriching;
    leadsDashboard.enrichedCount = e.detail.enrichedCount;
    leadsDashboard.totalCount = e.detail.totalCount;
    leadsDashboard.isWhatsAppEnriching = e.detail.isWhatsAppEnriching;
    leadsDashboard.whatsappEnrichedCount = e.detail.whatsappEnrichedCount;
    leadsDashboard.whatsappTotal = e.detail.whatsappTotal || e.detail.totalCount;
    leadsDashboard.lastWhatsAppError = e.detail.lastWhatsAppError;
  });

  leadsDashboard.addEventListener('start-whatsapp-batch', (e: any) => {
    playground.startWhatsAppBatch(e.detail);
  });

  leadsDashboard.addEventListener('start-enrichment', (e: any) => {
    playground.startEnrichment(e.detail);
  });

  leadsDashboard.addEventListener('request-lead-details', (e: any) => {
    playground.openLeadDetails(e.detail);
  });

  leadsDashboard.addEventListener('delete-leads', (e: any) => {
    const event = new CustomEvent('delete-leads', { detail: e.detail });
    playground.dispatchEvent(event);
  });

  // Tab switching logic
  const menuItems = header.querySelectorAll('.top-menu-item');
  menuItems.forEach(item => {
    item.addEventListener('click', () => {
      const tab = item.getAttribute('data-tab');
      menuItems.forEach(i => i.classList.remove('active'));
      item.classList.add('active');

      playground.classList.remove('active');
      leadsDashboard.classList.remove('active');
      regionSelector.classList.remove('active');
      targetSelector.classList.remove('active');
      campaignManager.classList.remove('active');
      jsonCentralView.classList.remove('active');

      if (tab === 'mapa') {
        playground.classList.add('active');
        if ((playground as any).refresh) (playground as any).refresh();
      } else if (tab === 'leads') {
        leadsDashboard.classList.add('active');
        // Exibe TODOS os leads no dashboard para permitir filtragem por campanha lá
        leadsDashboard.leads = leadService.getLeads();
        if (leadsDashboard.refresh) leadsDashboard.refresh();
        
        // @ts-ignore
        window.leadService = leadService;

        // Sincroniza em background, mas já mostra o que temos
        leadService.refresh().then(() => {
          leadsDashboard.leads = leadService.getLeads();
          if (leadsDashboard.refresh) leadsDashboard.refresh();
        });
      } else if (tab === 'regioes') {
        regionSelector.classList.add('active');
        if ((regionSelector as any).refresh) (regionSelector as any).refresh();
      } else if (tab === 'alvos') {
        targetSelector.classList.add('active');
        if ((targetSelector as any).refresh) (targetSelector as any).refresh();
      } else if (tab === 'campanhas') {
        campaignManager.classList.add('active');
        campaignManager.refreshCampaigns();
      } else if (tab === 'json-central') {
        jsonCentralView.classList.add('active');
        if (jsonCentralView.refreshHistory) jsonCentralView.refreshHistory();
      }
    });
  });

  // Evento global para troca de abas programática
  document.addEventListener('switch-tab', (e: any) => {
    const tabName = e.detail;
    const item = header.querySelector(`.top-menu-item[data-tab="${tabName}"]`) as HTMLElement;
    if (item) item.click();
  });

  // Evento de finalização de campanha
  document.addEventListener('finish-campaign', (e: any) => {
    const campaignId = e.detail;
    
    // Troca para a aba de campanhas
    const item = header.querySelector(`.top-menu-item[data-tab="campanhas"]`) as HTMLElement;
    if (item) item.click();

    // Seleciona a campanha recém criada para edição
    if (campaignManager.selectCampaignById) {
      campaignManager.selectCampaignById(campaignId);
    }
  });

  playground.renderMapQuery({ location: 'London' });


  // ---------

  const [transportA, transportB] = InMemoryTransport.createLinkedPair();

  void startMcpGoogleMapServer(transportA, (params: { location?: string, origin?: string, destination?: string, search?: string }) => {
    playground.renderMapQuery(params);
  });

  const mcpClient = await startClient(transportB);

  // --------

  // Sem chave, o chat nao existe e o resto do painel continua de pe. Antes, a
  // falta da chave levava a pagina inteira junto.
  let aiChat: ReturnType<typeof createAiChat> | null = null;
  try {
    aiChat = createAiChat(mcpClient);
  } catch (erro) {
    console.warn('[nyroh-leads]', erro instanceof Error ? erro.message : erro);
  }

  playground.sendMessageHandler = async (
    input: string,
    role: string,
  ) => {
    console.log(
      'sendMessageHandler',
      input,
      role
    );

    const { thinking, text } = playground.addMessage('assistant', '');
    const message = [];

    message.push({
      role,
      text: input,
    });

    playground.setChatState(ChatState.GENERATING);

    text.innerHTML = '...';

    let newCode = '';
    let thought = '';


    try {
      if (!aiChat) {
        // Tenta de novo: se a variavel foi preenchida e a pagina recarregada, a
        // construcao passa. Sem chave, esta linha lanca a mensagem que diz qual
        // variavel falta — e o catch abaixo a mostra na tela, em vez de o painel
        // travar em "..." para sempre.
        aiChat = createAiChat(mcpClient);
      }
      const res = await aiChat.sendMessageStream({ message });

      for await (const chunk of res) {
        for (const candidate of chunk.candidates ?? []) {
          for (const part of candidate.content?.parts ?? []) {
            if (part.functionCall) {
              console.log('FUNCTION CALL:', part.functionCall.name, part.functionCall.args);
              const mcpCall = {
                name: camelCaseToDash(part.functionCall.name!),
                arguments: part.functionCall.args
              };

              const explanation = 'Calling function:\n```json\n' + JSON.stringify(mcpCall, null, 2) + '\n```'
              const { thinking, text } = playground.addMessage('assistant', '');
              text.innerHTML = await marked.parse(explanation);
            }

            if (part.thought) {
              playground.setChatState(ChatState.THINKING);
              if (part.text) {
                thought += part.text;
                thinking.innerHTML = await marked.parse(thought);
                thinking.parentElement!.classList.remove('hidden');
              }
            } else if (part.text) {
              playground.setChatState(ChatState.EXECUTING);
              newCode += part.text;
              text.innerHTML = await marked.parse(newCode);
            }
            playground.scrollToTheEnd();
          }
        }
      }
    } catch (e: any) {
      console.error('GenAI SDK Error:', e.message);
      let message = e.message;
      const splitPos = e.message.indexOf('{');
      if (splitPos > -1) {
        const msgJson = e.message.substring(splitPos);
        try {
          const sdkError = JSON.parse(msgJson);
          if (sdkError.error) {
            message = sdkError.error.message;
            message = await marked.parse(message);
          }
        } catch (e) {
          console.error('Unable to parse the error message:', e);
        }
      }
      const { text } = playground.addMessage('error', '');
      text.innerHTML = message;
    }

    // close thinking block
    thinking.parentElement!.removeAttribute('open');

    // If the answer was just code
    if (text.innerHTML.trim().length === 0) {
      text.innerHTML = 'Done';
    }

    playground.setChatState(ChatState.IDLE);
  };

  playground.setInputField(
    EXAMPLE_PROMPTS[Math.floor(Math.random() * EXAMPLE_PROMPTS.length)],
  );
});
